import { Link, useParams } from "react-router-dom";

import { NotFoundPage } from "@/features/errors/NotFoundPage";
import {
  DocsLayout,
  DocsOpenFeature,
  DocsSeeAlso,
} from "@/features/help/DocsLayout";
import {
  canAccessGuide,
  getGuideBySlug,
} from "@/features/help/docsContent";
import { useAuth } from "@/auth/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";

function sectionId(heading) {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * MDN-style reference article for a single docs topic.
 */
export function DocsGuidePage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { can } = usePermissions();

  const guide = getGuideBySlug(slug);

  if (!canAccessGuide(guide, user, can)) {
    return <NotFoundPage />;
  }

  const relatedGuides = (guide.relatedSlugs || [])
    .map((relatedSlug) => getGuideBySlug(relatedSlug))
    .filter((related) => canAccessGuide(related, user, can));

  return (
    <DocsLayout
      title={guide.title}
      description={guide.summary}
      activeSlug={guide.slug}
      headerMode="article"
    >
      <article className="space-y-8">
        <header className="space-y-3 border-b pb-6">
          <p className="text-sm text-muted-foreground">
            <Link
              to="/docs"
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Docs
            </Link>
            <span className="mx-1.5 text-muted-foreground/70">/</span>
            <span>{guide.title}</span>
          </p>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 space-y-2">
              <h2 className="text-xl font-semibold tracking-tight">
                {guide.title}
              </h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {guide.summary}
              </p>
            </div>
            <DocsOpenFeature guide={guide} />
          </div>
        </header>

        {guide.sections.length > 1 && (
          <nav
            aria-label="On this page"
            className="rounded-md border bg-muted/30 px-4 py-3 text-sm"
          >
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              On this page
            </p>
            <ol className="list-decimal space-y-1 pl-4">
              {guide.sections.map((section) => (
                <li key={section.heading}>
                  <a
                    href={`#${sectionId(section.heading)}`}
                    className="text-primary underline-offset-4 hover:underline"
                  >
                    {section.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <div className="space-y-8">
          {guide.sections.map((section) => (
            <section
              key={section.heading}
              id={sectionId(section.heading)}
              className="scroll-mt-24 space-y-3"
            >
              <h3 className="text-base font-semibold tracking-tight">
                {section.heading}
              </h3>
              {section.paragraphs?.map((text) => (
                <p
                  key={text}
                  className="text-sm leading-relaxed text-muted-foreground"
                >
                  {text}
                </p>
              ))}
              {section.steps?.length > 0 && (
                <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                  {section.steps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              )}
              {section.tips?.length > 0 && (
                <div
                  className={cn(
                    "space-y-1.5 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground",
                  )}
                >
                  <p className="text-xs font-medium uppercase tracking-wide text-foreground">
                    Notes
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    {section.tips.map((tip) => (
                      <li key={tip}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ))}
        </div>

        <DocsSeeAlso guides={relatedGuides} />
      </article>
    </DocsLayout>
  );
}
